import { FormsMediaManager } from '@frontend/api-forms';
import { useMutation } from '@frontend/react-query-helpers';
import { useFillingExperienceContext } from '../../page-contents/filling-experience/provider';

interface UploadMediaVariables {
  file: File;
  signal?: AbortSignal;
}

/**
 * Uploads a single file to the forms media manager and returns the server media id.
 * Normalizes the api's `{ success: false }` result into a thrown error so react-query's
 * `onError`/rejection path fires and callers can drive retry/error UX.
 */
export const useUploadMediaMutation = () => {
  const { locationId } = useFillingExperienceContext();

  return useMutation<string, unknown, UploadMediaVariables>({
    mutationFn: async ({ file, signal }) => {
      if (!locationId) {
        throw new Error('Cannot upload media without a locationId');
      }

      const result = await FormsMediaManager.API.uploadMedia(file, locationId, { signal });

      if (!result.success || !result.fileId) {
        throw result.error ?? new Error(result.message ?? 'Failed to upload the file');
      }

      return result.fileId;
    },
  });
};

interface DeleteMediaVariables {
  mediaId: string;
}

/** Deletes a previously uploaded media item by its server id.
 *
 * `Requires authentication`
 */
export const useDeleteMediaMutation = () =>
  useMutation<string, unknown, DeleteMediaVariables>({
    mutationFn: async ({ mediaId }) => {
      const result = await FormsMediaManager.API.deleteMedia(mediaId);

      if (!result.success) {
        throw result.error ?? new Error(result.message ?? 'Failed to delete the file');
      }

      return mediaId;
    },
  });
